import type { Stop } from "../types/index";
import { getActiveTpid, getFeatureTypeName } from "./timetablePeriods";
import { API_ENDPOINTS } from "./apiEndpoints";
import { parseLineList } from "../utils/parseLineList";

// Define the geographical bounds interface
export interface Bounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

// Define the properties to request from the WFS service
function propNames(detailed = false) {
  const base = [
    "hplnr",
    "bytesprio",
    "hplnam",
    "linjelista",
    "geom",
    "tpid",
    "zon",
  ];
  return detailed ? base.concat(["alias"]) : base;
}

// Create XML for requested property names
function makePropertyXml(props: string[]) {
  return props.map((p) => `<wfs:PropertyName>${p}</wfs:PropertyName>`).join("");
}

// Build the WFS GetFeature XML payload
async function buildWfsPayload(
  bounds: Bounds | null,
  detailed = false,
  forcedTpid?: number | null
): Promise<string> {
  const tpidNum = forcedTpid ?? (await getActiveTpid());
  if (tpidNum == null) throw new Error("Ingen aktiv tidtabellsperiod hittades");
  const tpid = String(tpidNum);

  const propertyXml = makePropertyXml(propNames(detailed));
  const typeName = await getFeatureTypeName("Stops");

  const bboxXml = bounds
    ? `
        <ogc:BBOX>
          <ogc:PropertyName>geom</ogc:PropertyName>
          <gml:Box srsName="EPSG:4326" xmlns:gml="http://www.opengis.net/gml">
            <gml:coordinates>${bounds.west},${bounds.south} ${bounds.east},${bounds.north}</gml:coordinates>
          </gml:Box>
        </ogc:BBOX>`
    : "";

  const filterXml = `
      <ogc:Filter>
        <ogc:And>
          <ogc:PropertyIsEqualTo>
            <ogc:PropertyName>tpid</ogc:PropertyName>
            <ogc:Literal>${tpid}</ogc:Literal>
          </ogc:PropertyIsEqualTo>
          <ogc:PropertyIsGreaterThanOrEqualTo>
            <ogc:PropertyName>bytesprio</ogc:PropertyName>
            <ogc:Literal>0</ogc:Literal>
          </ogc:PropertyIsGreaterThanOrEqualTo>
          ${bboxXml}
        </ogc:And>
      </ogc:Filter>`.trim();

  const xml = `
  <GetFeature service="WFS" version="1.0.0" outputFormat="application/json"
    xmlns:wfs="http://www.opengis.net/wfs"
    xmlns:ogc="http://www.opengis.net/ogc"
    xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
    xsi:schemaLocation="http://www.opengis.net/wfs http://schemas.opengis.net/wfs/1.0.0/WFS-basic.xsd">
    <wfs:Query typeName="${typeName}">
      ${propertyXml}
      ${filterXml}
    </wfs:Query>
  </GetFeature>`.trim();

  return xml;
}

// Fetch stops from the map within the specified bounds
export async function fetchStopsFromMap(
  bounds: Bounds | null = null,
  detailed = false,
  forcedTpid?: number | null
): Promise<Stop[]> {
  const xml = await buildWfsPayload(bounds, detailed, forcedTpid);

  const res = await fetch(API_ENDPOINTS.GEO_FETCH, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
    },
    body: `=${encodeURIComponent(xml)}`,
  });

  if (!res.ok) {
    console.error("geo/fetch error:", res.status, await res.text());
    return [];
  }

  const geojson = await res.json();
  const feats: any[] = geojson?.features ?? [];

  return feats.map((feat) => {
    const props = feat?.properties ?? {};
    const [lng, lat] = feat?.geometry?.coordinates ?? [0, 0];

    const alias = String(props.alias ?? "").trim();
    const baseName = String(props.hplnam ?? "").trim();
    const name = alias ? `${baseName} (${alias})` : baseName;

    const servedLineIds = parseLineList(props.linjelista) ?? [];

    const rawBytesprio = Number(props.bytesprio);

    return {
      id: alias ? `${props.hplnr}-${alias}` : String(props.hplnr),
      name,
      lat,
      lng,
      type: alias || undefined,
      servedLineIds,
      bytesprio: Number.isFinite(rawBytesprio) ? rawBytesprio : 0,
    } as Stop;
  });
}

// Fetch all stops by dividing the area into a grid and fetching each section
export async function fetchAllStops(): Promise<Stop[]> {
  const tpid = await getActiveTpid();
  if (tpid == null) throw new Error("Ingen aktiv tidtabellsperiod hittades");

  const north = 59,
    south = 56,
    east = 17.5,
    west = 13;
  const step = 0.2;

  const jobs: Promise<Stop[]>[] = [];
  for (let lat = south; lat < north; lat += step) {
    for (let lng = west; lng < east; lng += step) {
      jobs.push(
        fetchStopsFromMap(
          {
            north: lat + step,
            south: lat,
            east: lng + step,
            west: lng,
          },
          false,
          tpid
        )
      );
    }
  }

  const results = await Promise.all(jobs);
  const merged: Record<string, Stop> = {};
  for (const list of results) {
    for (const stop of list ?? []) merged[stop.id] = stop;
  }
  return Object.values(merged);
}
